import React from "react";
import styled from "styled-components";
import Form from "react-bootstrap/Form";
import { useTranslation, Trans } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLockOpen,
  faThumbsUp,
  faGraduationCap,
  faCircleCheck,
} from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import {
  AppFlowContinueButton,
  AppFlowStepContainer,
  AppFlowStepContentContainer,
  AppFlowModal,
} from "../../common/CommonStyles";
import {
  Account,
  AccountContext,
  RetryBehavior,
  MAX_PROFILES_PER_USER,
} from "../../common/context/AccountContext";
import {
  AnalyticsContext,
  ChoosePurchaseEvent,
  PurchaseButtonTapEvent,
  RedeemOfferCodeButtonTapEvent,
  ViewPurchaseChoiceEvent,
} from "../../common/context/AnalyticsContext";
import { useLiveData } from "../../common/utils";
import {
  StripeSubscriptionProduct,
  StripeSubscriptionProductId,
  ValidateOfferCodeResponse,
} from "../../common/api";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { ApiContext } from "../../common/context/ApiContext";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_WHITE,
  COLOR_GRAY_E8,
  COLOR_GRAY_CC,
  COLOR_GRAY_77,
  COLOR_GRAY_50,
  COLOR_GRAY_DISABLED,
  COLOR_NEUTRAL_BORDER,
  COLOR_PAYWALL_HIGHLIGHT,
} from "../../common/colors";

const LIMITED_HEIGHT = "@media (max-height: 900px)";

const FreeTrialUpsell = styled.div`
  display: grid;
  column-gap: 15px;
  grid-template-columns: 50px minmax(20ch, 30ch);
  grid-template-rows: repeat(2, minmax(auto, 3fr) minmax(auto, 2fr));
  width: auto;

  ${LIMITED_HEIGHT} {
    grid-template-columns: 40px minmax(20ch, 30ch);
  }
`;

// Holds the timeline, the plan cards and the "no payment due" line.
const FreeTrialColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  gap: 24px;
  padding: 0;

  ${LIMITED_HEIGHT} {
    gap: 12px;
  }
`;

const FreeTrialUpsellReason = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FreeTrialReasonTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${COLOR_TEXT_PRIMARY};
`;

const FreeTrialReasonText = styled.div`
  font-size: 0.85rem;
  color: ${COLOR_TEXT_SECONDARY};
`;

const FreeTrialIconCircle = styled.div<{ highlighted: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background-color: ${(p) => (p.highlighted ? COLOR_TEXT_PRIMARY : COLOR_GRAY_E8)};
  color: ${(p) => (p.highlighted ? COLOR_WHITE : COLOR_TEXT_PRIMARY)};

  ${LIMITED_HEIGHT} {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const Line = styled.div`
  width: 5px;

  justify-self: center;
  margin: -15px 0px;
  z-index: -1;
`;

const FreeTrialHalfAndHalfLine = styled(Line)`
  background: linear-gradient(to top, ${COLOR_GRAY_E8} 50%, ${COLOR_TEXT_PRIMARY} 50%);
`;

const FreeTrialGrayLine = styled(Line)`
  background: ${COLOR_GRAY_E8};
`;

const BannerContainer = styled.div`
  position: relative;

  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  height: 33vh;
  min-height: 200px;

  clip-path: url(#curved-bottom-clip);
  -webkit-clip-path: url(#curved-bottom-clip);
  overflow: hidden;

  @media (min-width: 600px) {
    height: 40vh;
  }

  ${LIMITED_HEIGHT} {
    height: 24vh;
    min-height: 140px;
  }
`;

const ProductContainer = styled.div<{ isSelected: boolean }>`
  max-width: 500px;
  width: 85vw;
  height: 10vh;
  min-height: 60px;
  cursor: pointer;

  border: ${(p) => (p.isSelected ? `2px solid ${COLOR_TEXT_PRIMARY}` : `2px solid ${COLOR_GRAY_CC}`)};
  color: ${(p) => (p.isSelected ? COLOR_TEXT_PRIMARY : COLOR_GRAY_77)};
  background-color: ${COLOR_WHITE};
  border-radius: 20px;
  position: relative;
  overflow: hidden;
`;

const BannerGridContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  opacity: 20%;

  background-image: url(${process.env.PUBLIC_URL}/lesson_cover_grid_banner.jpg);
  background-repeat: repeat;
  background-size: 734px auto;
`;

const BannerTeacherContainer = styled.div`
  position: absolute;
  bottom: 0;

  z-index: 3;
  height: 80%;
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: flex-end;

  & > img {
    width: auto;
    height: 100%;
  }
`;

const PLAN_BADGE_OVERHANG_PX = 10;

const PlanCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 300px;
  max-width: 100%;
  padding-top: ${PLAN_BADGE_OVERHANG_PX}px;
`;

const PlanCard = styled.button<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 96px;
  padding: 14px 16px;
  text-align: left;
  border-radius: 12px;
  cursor: pointer;
  border: 2px solid
    ${(p) => (p.isSelected ? COLOR_TEXT_PRIMARY : COLOR_NEUTRAL_BORDER)};
  background: ${(p) => (p.isSelected ? COLOR_GRAY_50 : COLOR_WHITE)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  ${LIMITED_HEIGHT} {
    min-height: 84px;
    padding: 10px 12px;
  }
`;

const PlanTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const PlanTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${COLOR_TEXT_PRIMARY};
`;

const PlanRadio = styled.div<{ isSelected: boolean }>`
  flex-shrink: 0;
  font-size: 1.1rem;
  line-height: 1;
  color: ${(p) => (p.isSelected ? COLOR_TEXT_PRIMARY : COLOR_GRAY_DISABLED)};
`;

const PlanPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
`;

const PlanPrice = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${COLOR_TEXT_PRIMARY};
`;

const PlanPeriod = styled.div`
  font-size: 0.75rem;
  color: ${COLOR_TEXT_SECONDARY};
`;

const PlanEquivalentPrice = styled.div`
  font-size: 0.75rem;
  color: ${COLOR_TEXT_SECONDARY};
`;

const PlanSavingsBadge = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  height: ${PLAN_BADGE_OVERHANG_PX * 2}px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  border-radius: ${PLAN_BADGE_OVERHANG_PX}px;
  white-space: nowrap;
  background-color: ${COLOR_PAYWALL_HIGHLIGHT};
  color: ${COLOR_WHITE};
  font-size: 0.7rem;
  font-weight: 600;
`;

const NoPaymentDueText = styled.div`
  font-size: 0.85rem;
  color: ${COLOR_TEXT_PRIMARY};
`;

// How much cheaper a year of annual is than twelve months of monthly, rounded down — at
// $19.99/mo against $99.99/yr that's 58%.
const annualSavingsPercent = (
  monthlyCents: number,
  annualCents: number
): number | null => {
  const yearOfMonthlyCents = monthlyCents * 12;
  if (
    yearOfMonthlyCents <= 0 ||
    annualCents <= 0 ||
    annualCents >= yearOfMonthlyCents
  ) {
    return null;
  }
  const percent = Math.floor(
    ((yearOfMonthlyCents - annualCents) / yearOfMonthlyCents) * 100
  );
  return percent >= 1 && percent <= 99 ? percent : null;
};

export const PurchaseChoice: React.FC<{
  next: (selectedProductId?: StripeSubscriptionProductId) => void;
}> = ({ next }) => {
  const { t } = useTranslation();
  const [selectedSubscriptionProductId, setSelectedSubscriptionProductId] =
    React.useState<StripeSubscriptionProductId>(
      StripeSubscriptionProductId.Annual
    );
  const [offerCodeModalState, setOfferCodeModalState] = React.useState({
    isVisible: false,
    code: "",
  });
  const [offerCodeResponseModalState, setOfferCodeResponseModalState] =
    React.useState({
      isVisible: false,
      responseTitle: "",
      responseMessage: "",
      isCodeRecognized: false,
      responseImageUrl: "",
    });

  const { getAvailableSubscriptions } = React.useContext(ApiContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { accountLiveData, makeRequest, updateUser } =
    React.useContext(AccountContext);
  const [account, setAccount] = React.useState<Account>();
  const { logEvent } = React.useContext(AnalyticsContext);
  const shouldLogViewPurchaseChoice = React.useRef<boolean>(true);
  const [shouldShowFreeTrialUI, setShouldShowFreeTrialUI] =
    React.useState<boolean>(true);
  const [subscriptions, setSubscriptions] = React.useState<
    StripeSubscriptionProduct[] | null
  >(null);

  const getSubscriptionText = React.useCallback(
    (product_id: StripeSubscriptionProductId): React.ReactElement => {
      if (subscriptions === null) return <span>""</span>;

      const subscription = subscriptions.find(
        (s) => s.product_id === product_id
      )!!;

      if (product_id === StripeSubscriptionProductId.Annual) {
        const price_per_year = subscription.price_usd_cents / 100;
        if (subscription.free_trial_days > 0) {
          return (
            <Trans
              t={t}
              i18nKey="purchase_choice_pricing_free_period_then_regular_period_year"
              components={{ br: <br /> }}
              values={{
                free_days: t("purchase_choice_free_days", {
                  count: subscription.free_trial_days,
                }),
                price_per_year: price_per_year,
                price_per_month: (price_per_year / 12).toFixed(2),
              }}
            />
          );
        } else {
          return (
            <span>
              {t("purchase_choice_pricing_annual_per_year", {
                price_per_year: price_per_year,
              })}
            </span>
          );
        }
      } else if (product_id === StripeSubscriptionProductId.Monthly) {
        const price_per_month = (subscription.price_usd_cents / 100).toFixed(2);
        if (subscription.free_trial_days > 0) {
          return (
            <Trans
              t={t}
              i18nKey="purchase_choice_pricing_free_period_then_regular_period_month"
              components={{ br: <br /> }}
              values={{
                free_days: t("purchase_choice_free_days", {
                  count: subscription.free_trial_days,
                }),
                price_per_month: price_per_month,
              }}
            />
          );
        } else {
          return (
            <span>
              {t("purchase_choice_pricing_annual_per_month", {
                price_per_month: price_per_month,
              })}
            </span>
          );
        }
      }
      return <span>""</span>;
    },
    [subscriptions, t]
  );

  useLiveData(
    React.useCallback(
      async (account: Account) => {
        if (account.hasUserAndMaybeProfile()) {
          setAccount(account);
          if (subscriptions === null) {
            startLoading();
            const subscriptionsResponse = await getAvailableSubscriptions();
            setShouldShowFreeTrialUI(
              subscriptionsResponse.available_subscriptions.some(
                (s) => s.free_trial_days >= 7
              )
            );
            setSubscriptions(subscriptionsResponse.available_subscriptions);
            stopLoading();
          }
        }
      },
      [
        subscriptions,
        setSubscriptions,
        getAvailableSubscriptions,
        startLoading,
        stopLoading,
        setAccount,
      ]
    ),
    accountLiveData
  );

  const validateOfferCode = React.useCallback(async () => {
    const code = offerCodeModalState.code.trim().toUpperCase();
    if (code.length === 0) {
      return;
    }

    const response = await makeRequest(
      "purchase/validate-offer-code/",
      "POST",
      RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
      true,
      { code }
    );

    const newOfferCodeResponseModalState = {
      isVisible: true,
      responseTitle: "",
      responseMessage: "",
      isCodeRecognized: false,
      responseImageUrl: "",
    };

    if (!response.isSuccessful) {
      setOfferCodeResponseModalState(newOfferCodeResponseModalState);
      setOfferCodeModalState({ isVisible: false, code: "" });
      return;
    }

    const validateOfferCodeResponse = response.body as ValidateOfferCodeResponse;

    if (
      validateOfferCodeResponse.result === "ok" &&
      validateOfferCodeResponse.user !== null &&
      validateOfferCodeResponse.user !== undefined &&
      !validateOfferCodeResponse.enable_store_redemption
    ) {
      updateUser(validateOfferCodeResponse.user, true, null, false);

      newOfferCodeResponseModalState.isCodeRecognized = true;
      newOfferCodeResponseModalState.responseTitle =
        validateOfferCodeResponse.title;
      newOfferCodeResponseModalState.responseMessage =
        validateOfferCodeResponse.message;
      newOfferCodeResponseModalState.responseImageUrl =
        validateOfferCodeResponse.image_url ?? "";
    } else {
      newOfferCodeResponseModalState.isCodeRecognized = false;
      newOfferCodeResponseModalState.responseMessage =
        validateOfferCodeResponse.error_message;
    }

    setOfferCodeResponseModalState(newOfferCodeResponseModalState);
    setOfferCodeModalState({ isVisible: false, code: "" });
  }, [makeRequest, offerCodeModalState.code, updateUser]);

  if (!account || !subscriptions) {
    return <></>;
  }

  const renderCheckCircle = (isSelected: boolean) => (
    <div
      style={{
        backgroundColor: isSelected ? COLOR_TEXT_PRIMARY : "transparent",
        border: isSelected ? "none" : `2px solid ${COLOR_GRAY_CC}`,
        borderRadius: "50%",
        color: isSelected ? COLOR_WHITE : "transparent",
        width: "3.5vh",
        height: "3.5vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1rem",
      }}
    >
      {isSelected ? "✓" : "&nbsp"}
    </div>
  );

  const monthlySubscription = subscriptions.find(
    (s) => s.product_id === StripeSubscriptionProductId.Monthly
  );
  const annualSubscription = subscriptions.find(
    (s) => s.product_id === StripeSubscriptionProductId.Annual
  );

  // The server lists annual first, but the cards read monthly then annual, left to right.
  const orderedSubscriptions = [monthlySubscription, annualSubscription].filter(
    (s): s is StripeSubscriptionProduct => s !== undefined
  );

  const savingsPercent =
    monthlySubscription && annualSubscription
      ? annualSavingsPercent(
          monthlySubscription.price_usd_cents,
          annualSubscription.price_usd_cents
        )
      : null;

  const renderPlanCard = (s: StripeSubscriptionProduct) => {
    const isAnnual = s.product_id === StripeSubscriptionProductId.Annual;
    const isSelected = selectedSubscriptionProductId === s.product_id;
    const price = s.price_usd_cents / 100;

    return (
      <PlanCard
        key={"plan-card-" + s.product_id}
        type="button"
        isSelected={isSelected}
        aria-pressed={isSelected}
        onClick={() => {
          logEvent(new ChoosePurchaseEvent(s.product_id));
          setSelectedSubscriptionProductId(s.product_id);
        }}
      >
        {isAnnual && savingsPercent !== null && (
          <PlanSavingsBadge>
            {t("paywall_save_badge", { percent: savingsPercent })}
          </PlanSavingsBadge>
        )}
        <PlanTitleRow>
          <PlanTitle>
            {isAnnual
              ? t("purchase_choice_product_annual")
              : t("purchase_choice_product_monthly")}
          </PlanTitle>
          <PlanRadio isSelected={isSelected}>
            <FontAwesomeIcon icon={isSelected ? faCircleCheck : faCircle} />
          </PlanRadio>
        </PlanTitleRow>
        <PlanPriceRow>
          <PlanPrice>${price.toFixed(2)}</PlanPrice>
          <PlanPeriod>
            {isAnnual
              ? t("paywall_period_suffix_year")
              : t("paywall_period_suffix_month")}
          </PlanPeriod>
        </PlanPriceRow>
        {/* The monthly card keeps a blank line so both cards are the same height. */}
        <PlanEquivalentPrice>
          {isAnnual
            ? t("purchase_choice_pricing_annual_per_month", {
                price_per_month: (price / 12).toFixed(2),
              })
            : "\u00a0"}
        </PlanEquivalentPrice>
      </PlanCard>
    );
  };

  if (shouldLogViewPurchaseChoice.current) {
    logEvent(new ViewPurchaseChoiceEvent());
    shouldLogViewPurchaseChoice.current = false;
  }

  return (
    <>
      <AppFlowModal
        title={t("offer_code_modal_title")}
        body={
          <Form>
            <Form.Control
              type="text"
              placeholder={t("offer_code_modal_hint")}
              maxLength={36}
              value={offerCodeModalState.code}
              onChange={(e) =>
                setOfferCodeModalState((prev) => ({
                  ...prev,
                  code: e.target.value,
                }))
              }
            />
          </Form>
        }
        closeButtonText={t("generic_cancel")}
        shouldShowModal={offerCodeModalState.isVisible}
        closeModal={() =>
          setOfferCodeModalState((prev) => ({
            ...prev,
            isVisible: false,
          }))
        }
        submitButtonText={t("generic_submit")}
        onClickSubmit={() => validateOfferCode()}
        isSubmitEnabled={offerCodeModalState.code.trim().length > 0}
      />
      <AppFlowModal
        title={
          offerCodeResponseModalState.isCodeRecognized
            ? offerCodeResponseModalState.responseTitle
            : t("offer_code_not_recognized")
        }
        body={
          <>
            {offerCodeResponseModalState.responseMessage}
            {offerCodeResponseModalState.responseImageUrl && (
              <img
                src={offerCodeResponseModalState.responseImageUrl}
                alt=""
                style={{
                  display: "block",
                  marginTop: "12px",
                  maxWidth: "100%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              />
            )}
          </>
        }
        closeButtonText={
          offerCodeResponseModalState.isCodeRecognized
            ? t("offer_code_modal_close_button_title")
            : t("generic_ok")
        }
        shouldShowModal={offerCodeResponseModalState.isVisible}
        closeModal={() => {
          setOfferCodeResponseModalState((prev) => ({
            ...prev,
            isVisible: false,
          }));
          if (offerCodeResponseModalState.isCodeRecognized) {
            next();
          }
        }}
      />
      <AppFlowStepContainer
        extraClassName="py-0 px-3 mb-3"
        style={{ height: "100%" }}
      >
        <BannerContainer>
          <BannerGridContainer />
          <BannerTeacherContainer>
            <img
              alt=""
              src={`${process.env.PUBLIC_URL}/teacher_wave_plain.png`}
            />
          </BannerTeacherContainer>
        </BannerContainer>
        {/*
	    This svg is intentionally invisible but used to crop banner above
	    by being referenced using the id in the clip-path css attribute
	    on the banner
	  */}
        <svg width="0" height="0">
          <defs>
            <clipPath id="curved-bottom-clip" clipPathUnits="objectBoundingBox">
              <path d="M0 0 1 0 1 .92Q.5 1.08 0 .92Z" />
            </clipPath>
          </defs>
        </svg>
        <AppFlowStepContentContainer extraClassName="mt-3">
          {shouldShowFreeTrialUI ? (
            <FreeTrialColumn>
              <div className="flex-grow-1 d-flex w-100">
                <FreeTrialUpsell className="mx-auto my-auto">
                  <div className="d-flex align-items-center">
                    <FreeTrialIconCircle highlighted={true}>
                      <FontAwesomeIcon icon={faLockOpen} />
                    </FreeTrialIconCircle>
                  </div>
                  <FreeTrialUpsellReason>
                    <FreeTrialReasonTitle>
                      {t("free_trial_try_it_free_subtitle")}
                    </FreeTrialReasonTitle>
                    <FreeTrialReasonText>
                      {t("free_trial_try_it_free_content", {
                        max_profiles: MAX_PROFILES_PER_USER,
                      })}
                    </FreeTrialReasonText>
                  </FreeTrialUpsellReason>
                  <FreeTrialHalfAndHalfLine></FreeTrialHalfAndHalfLine>
                  <div>{/*intentionally empty */}</div>
                  <div className="d-flex align-items-center">
                    <FreeTrialIconCircle highlighted={false}>
                      <FontAwesomeIcon icon={faThumbsUp} />
                    </FreeTrialIconCircle>
                  </div>
                  <FreeTrialUpsellReason>
                    <FreeTrialReasonTitle>
                      {t("free_trial_enjoy_the_app_subtitle")}
                    </FreeTrialReasonTitle>
                    <FreeTrialReasonText>
                      {t("free_trial_enjoy_the_app_content")}
                    </FreeTrialReasonText>
                  </FreeTrialUpsellReason>
                  <FreeTrialGrayLine></FreeTrialGrayLine>
                  <div>{/*intentionally empty */}</div>
                  <div className="d-flex align-items-center">
                    <FreeTrialIconCircle highlighted={false}>
                      <FontAwesomeIcon icon={faGraduationCap} />
                    </FreeTrialIconCircle>
                  </div>
                  <FreeTrialUpsellReason>
                    <FreeTrialReasonTitle>
                      {t("free_trial_trial_ends_subtitle")}
                    </FreeTrialReasonTitle>
                    <FreeTrialReasonText>
                      {t("free_trial_trial_ends_content")}
                    </FreeTrialReasonText>
                  </FreeTrialUpsellReason>
                </FreeTrialUpsell>
              </div>
              <div className="d-flex align-items-center justify-content-center flex-column gap-2">
                <PlanCardsRow>
                  {orderedSubscriptions.map((s) => renderPlanCard(s))}
                </PlanCardsRow>
                <NoPaymentDueText>
                  {t("paywall_no_payment_due")}
                </NoPaymentDueText>
              </div>
            </FreeTrialColumn>
          ) : (
            <div
              className="d-flex flex-column align-items-center flex-grow-1"
              style={{ padding: 0 }}
            >
              <h1 className="fw-bold">{t("no_free_trial_title")}</h1>
              <span>{t("no_free_trial_helper_text")}</span>
              <div className="d-flex flex-column flex-grow-1 align-items-center justify-content-center gap-3 pb-5 mb-5">
                {subscriptions.map((s) => (
                  <ProductContainer
                    key={"sub-col-" + s.product_id}
                    className="d-flex p-3"
                    isSelected={selectedSubscriptionProductId === s.product_id}
                    onClick={() => {
                      logEvent(new ChoosePurchaseEvent(s.product_id));
                      setSelectedSubscriptionProductId(s.product_id);
                    }}
                  >
                    <div className="w-100 d-flex flex-grow-1 align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        {renderCheckCircle(
                          selectedSubscriptionProductId === s.product_id
                        )}
                        <span className="fs-3 fw-bold">
                          {s.product_id === StripeSubscriptionProductId.Annual
                            ? t("purchase_choice_product_annual")
                            : t("purchase_choice_product_monthly")}
                        </span>
                      </div>
                      <span className="fs-3 fw-bold">
                        {getSubscriptionText(s.product_id)}
                      </span>
                    </div>
                  </ProductContainer>
                ))}
              </div>
            </div>
          )}
          <div className="d-flex align-items-center justify-content-center">
            <button
              className="btn btn-link fs-6 p-0 text-decoration-none"
              style={{
                cursor: "pointer",
                lineHeight: "22px",
              }}
              onClick={() => {
                logEvent(new RedeemOfferCodeButtonTapEvent());
                setOfferCodeModalState((prev) => ({
                  ...prev,
                  isVisible: true,
                }));
              }}
            >
              {t("free_trial_enter_code")}
            </button>
          </div>
          <div className="d-flex justify-content-center p-2">
            <AppFlowContinueButton
              text={
                shouldShowFreeTrialUI
                  ? t("free_trial_continue_button")
                  : t("no_free_trial_continue_button")
              }
              enabled={true}
              onClick={() => {
                logEvent(new PurchaseButtonTapEvent());
                next(selectedSubscriptionProductId);
              }}
            />
          </div>
        </AppFlowStepContentContainer>
      </AppFlowStepContainer>
    </>
  );
};
